import React, { useState, useEffect, useCallback, useRef } from 'react';

// components
import { General } from 'icons/untitledui';

// styles
import * as SearchModalStyles from 'theme/components/SearchModal.style';
import {
  SearchInputWrapper,
  SearchInputIcon,
  SearchInput,
  NoResults,
} from 'theme/components/Dropdown.style';
import { SecondaryLightIcon } from 'theme/components/Buttons.style';

interface RenderRowContext {
  isSelected: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
  ref: (el: HTMLElement | null) => void;
}

interface SearchModalProps<T> {
  isOpen: boolean;
  onClose: () => void;
  placeholder: string;
  noResultsText: string;
  filterItems: (query: string) => T[];
  getItemKey: (item: T, index: number) => string;
  renderItem: (item: T, searchQuery: string) => React.ReactNode;
  onItemSelect: (item: T) => void;
  onQueryChange?: (query: string) => void;
  renderRow?: (
    item: T,
    searchQuery: string,
    ctx: RenderRowContext
  ) => React.ReactNode;
}

export default function SearchModal<T>({
  isOpen,
  onClose,
  placeholder,
  noResultsText,
  filterItems,
  getItemKey,
  renderItem,
  onItemSelect,
  onQueryChange,
  renderRow,
}: SearchModalProps<T>): JSX.Element | null {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const entryRefs = useRef<(HTMLElement | null)[]>([]);

  const filteredItems = filterItems(searchQuery);

  useEffect(() => {
    onQueryChange?.(searchQuery);
  }, [searchQuery, onQueryChange]);

  useEffect(() => {
    setSelectedIndex(-1);
    entryRefs.current = [];
  }, [filteredItems.length, searchQuery]);

  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < entryRefs.current.length) {
      entryRefs.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    onClose();
  }, [onClose]);

  const handleItemSelect = useCallback(
    (item: T) => {
      onItemSelect(item);
      onClose();
      setSearchQuery('');
    },
    [onItemSelect, onClose]
  );

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (filteredItems.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredItems.length - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredItems.length - 1
        );
        return;
      }

      if (
        e.key === 'Enter' &&
        selectedIndex >= 0 &&
        selectedIndex < filteredItems.length
      ) {
        e.preventDefault();
        handleItemSelect(filteredItems[selectedIndex]);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setSelectedIndex(-1);
      setSearchQuery('');
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose, filteredItems, selectedIndex, handleItemSelect]);

  if (!isOpen) return null;

  return (
    <>
      <SearchModalStyles.Backdrop
        $isOpen={isOpen}
        onClick={handleBackdropClick}
      />
      <SearchModalStyles.Container
        $isOpen={isOpen}
        onClick={handleBackdropClick}
      >
        <SearchModalStyles.Card onClick={(e) => e.stopPropagation()}>
          <SearchModalStyles.Header>
            <SearchInputWrapper>
              <SearchInputIcon>
                <General.SearchSm size={20} />
              </SearchInputIcon>
              <SearchInput
                type="text"
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </SearchInputWrapper>
            <SecondaryLightIcon
              onClick={handleClose}
              aria-label="Close search"
              medium
            >
              <General.XClose size={20} />
            </SecondaryLightIcon>
          </SearchModalStyles.Header>

          <SearchModalStyles.ResultsList>
            {filteredItems.length === 0 ? (
              <NoResults>{noResultsText}</NoResults>
            ) : (
              filteredItems.map((item, idx) => {
                const setRef = (el: HTMLElement | null) => {
                  entryRefs.current[idx] = el;
                };
                const onMouseEnter = () => setSelectedIndex(idx);
                const onSelect = () => handleItemSelect(item);
                const key = getItemKey(item, idx);
                if (renderRow) {
                  return (
                    <React.Fragment key={key}>
                      {renderRow(item, searchQuery, {
                        isSelected: idx === selectedIndex,
                        onSelect,
                        onMouseEnter,
                        ref: setRef,
                      })}
                    </React.Fragment>
                  );
                }
                return (
                  <SearchModalStyles.ResultRow
                    key={key}
                    ref={setRef}
                    $isSelected={idx === selectedIndex}
                    onMouseEnter={onMouseEnter}
                    onClick={onSelect}
                  >
                    {renderItem(item, searchQuery)}
                  </SearchModalStyles.ResultRow>
                );
              })
            )}
          </SearchModalStyles.ResultsList>
        </SearchModalStyles.Card>
      </SearchModalStyles.Container>
    </>
  );
}
